import { FC } from 'react'
import { EChartOption } from 'echarts'
import { EChart } from 'src/charts/EChart'
import styles from './dailyMentions.module.scss'
import { DailyMentionsData } from '../interfaces/comparison.interface'

interface DailyMentionsProps {
  data: DailyMentionsData
}

const COPY = {
  // Both series flat at zero draws a chart that looks like a rendering fault.
  // Saying there was nothing to plot is the honest version of the same fact.
  empty:
    'No mentions were collected in this period, so there is nothing to chart.',
  // Figma said "by Day of Month", which is not what the axis is: the backend
  // buckets by day offset from each window's own start, so two periods that do
  // not begin on the 1st still overlay correctly.
  title: 'Daily Mentions — Overlaid by Day of Period',
}

// The two periods keep the legend's colours from Key Metrics: the primary in
// grey, the comparison in blue.
const PRIMARY_COLOR = '#71717a'
const COMPARISON_COLOR = '#3b82f6'

const CHART_HEIGHT = 273

// Figma fades each series to almost nothing at the baseline, so the two areas
// can overlap without the lower one being lost under the upper.
const areaGradient = (rgb: string): EChartOption.SeriesLine['areaStyle'] => ({
  color: {
    colorStops: [
      { color: `rgba(${rgb}, 0.337255)`, offset: 0 },
      { color: `rgba(${rgb}, 0.05)`, offset: 1 },
    ],
    // `global` belongs to the bundled LinearGradient type; false keeps the
    // stops relative to the shape, which is what a top-to-bottom fade needs.
    global: false,
    type: 'linear',
    x: 0,
    x2: 0,
    y: 0,
    y2: 1,
  },
})

export const DailyMentions: FC<DailyMentionsProps> = ({ data }) => {
  const series = [
    { color: PRIMARY_COLOR, rgb: '113, 113, 122', row: data.primary },
    { color: COMPARISON_COLOR, rgb: '59, 130, 246', row: data.comparison },
  ]

  // Nothing on either side. Not "no days" — a window always has days — but no
  // mention on any of them, for either campaign.
  const isEmpty = series.every(({ row }) =>
    (row.values || []).every((value) => !value)
  )

  const options: EChartOption = {
    grid: {
      // The legend is drawn inside the canvas here, so the plot reserves its
      // height — at 0 the day labels and the legend sat on top of each other.
      bottom: 36,
      containLabel: true,
      // The first and last points sit on the axis ends, so their labels need
      // half a label's width of room or they are clipped by the canvas. Sized
      // for the widest of them — a bucket spanning several days is labelled as
      // a range ("Day 29–31"), and at 24 the last one lost its final digit.
      left: 36,
      right: 36,
      top: 8,
    },
    legend: {
      bottom: 0,
      // ECharts draws a line series' marker as a line with a dot, which is the
      // marker the mock uses.
      itemGap: 24,
      itemWidth: 24,
      textStyle: {
        color: '#25262d',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 14,
      },
    },
    series: series.map(({ color, rgb, row }) => ({
      areaStyle: areaGradient(rgb),
      data: row.values,
      itemStyle: { color },
      lineStyle: { color, width: 1.5 },
      name: row.label,
      // Straight segments between the days, as drawn in Figma.
      smooth: false,
      symbol: 'circle',
      symbolSize: 8,
      type: 'line',
    })),
    tooltip: {
      axisPointer: { type: 'none' },
      trigger: 'axis',
    },
    xAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        fontWeight: 400,
        margin: 16,
      },
      axisLine: { lineStyle: { color: '#25262d' } },
      axisTick: { show: false },
      // The line starts on the axis rather than inset, so the first day's area
      // meets the y-axis as it does in the mock.
      boundaryGap: false,
      data: data.days,
      splitLine: {
        lineStyle: { color: '#e6ebfa', type: 'dashed' },
        show: true,
      },
      type: 'category',
    },
    yAxis: {
      axisLabel: {
        color: '#000000b2',
        fontFamily: 'Roboto, sans-serif',
        fontSize: 12,
        fontWeight: 400,
        formatter: (value: number) => `${value}`,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      // As in Sentiment Split: the scale follows the data rather than the
      // placeholder range the mock was drawn with.
      min: 0,
      splitLine: { lineStyle: { color: '#e6ebfa', type: 'dashed' } },
      type: 'value',
    },
  }

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>{COPY.title}</h3>

      {isEmpty ? (
        <p className={styles.empty}>{COPY.empty}</p>
      ) : (
        <div className={styles.chart}>
          <EChart height={CHART_HEIGHT} options={options} />
        </div>
      )}
    </div>
  )
}
